import { useState, useEffect, type CSSProperties } from "react";
import type { Task, TaskStatus } from "../../../lib/types";
import { useUpdateTask, useDeleteTask } from "../../hooks/use-task-mutations";
import { showToast, ConfirmModal } from "../primitives";
import {
  modalOverlay, modalBox, fieldStyle, labelStyle, inputStyle,
  STATUSES, STATUS_LABEL,
} from "./task-modal-shared";

interface TaskEditModalProps {
  open: boolean;
  onClose: () => void;
  task: Task;
  onDeleted?: (task: Task) => void;
}

const BODY_LIMIT = 2000;

/**
 * Edit an existing task (Sprint 26.0). Same fields as NewTaskModal,
 * pre-populated, plus a status picker (inbox/queued/done/dropped) and a delete
 * action with confirmation. Mutations route through the task's owning agent.
 */
export function TaskEditModal({ open, onClose, task, onDeleted }: TaskEditModalProps) {
  const [title, setTitle] = useState(task.title);
  const [body, setBody] = useState(task.body ?? "");
  const [status, setStatus] = useState<TaskStatus>(task.status);
  const [dueBy, setDueBy] = useState(task.due_by ?? "");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();
  const nodeId = task.agent_id ?? "";

  useEffect(() => {
    if (open) {
      setTitle(task.title);
      setBody(task.body ?? "");
      setStatus(task.status);
      setDueBy(task.due_by ?? "");
    }
  }, [open, task]);

  if (!open) return null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    updateTask.mutate(
      {
        node_id: nodeId,
        task_id: task.id,
        title: title.trim(),
        body: body.trim(),
        status,
        due_by: dueBy,
      },
      {
        onSuccess: () => {
          showToast("Task updated");
          onClose();
        },
        onError: (err) => {
          const msg = err instanceof Error ? err.message : "Something went wrong";
          showToast(`Couldn't update task: ${msg}`, "error", true);
        },
      },
    );
  }

  function handleDelete() {
    deleteTask.mutate({ node_id: nodeId, task_id: task.id }, {
      onSuccess: () => {
        showToast("Task deleted");
        setConfirmDelete(false);
        onDeleted?.(task);
        onClose();
      },
      onError: (err) => {
        const msg = err instanceof Error ? err.message : "Something went wrong";
        showToast(`Couldn't delete task: ${msg}`, "error", true);
        setConfirmDelete(false);
      },
    });
  }

  const deleteBtn: CSSProperties = {
    fontSize: 13,
    padding: "8px 14px",
    color: "var(--c-red-600)",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    marginRight: "auto",
  };

  return (
    <div style={modalOverlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modalBox} role="dialog" aria-label="Edit task">
        <div style={{ fontSize: 15, fontWeight: 500, color: "var(--color-text-primary)", marginBottom: 20 }}>
          Edit task
        </div>

        <form onSubmit={handleSubmit}>
          <div style={fieldStyle}>
            <label style={labelStyle}>Title</label>
            <input
              type="text"
              style={inputStyle}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Details</label>
            <textarea
              style={{ ...inputStyle, minHeight: 96, resize: "vertical" }}
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, BODY_LIMIT))}
              placeholder="The task document. Markdown supported."
              maxLength={BODY_LIMIT}
            />
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ ...fieldStyle, flex: 1 }}>
              <label style={labelStyle}>Status</label>
              <select
                style={inputStyle}
                value={status}
                onChange={(e) => setStatus(e.target.value as TaskStatus)}
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                ))}
              </select>
            </div>
            <div style={{ ...fieldStyle, flex: 1 }}>
              <label style={labelStyle}>Due date</label>
              <input
                type="date"
                style={inputStyle}
                value={dueBy}
                onChange={(e) => setDueBy(e.target.value)}
              />
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 20 }}>
            <button type="button" onClick={() => setConfirmDelete(true)} style={deleteBtn}>
              Delete
            </button>
            <button type="button" onClick={onClose} style={{ fontSize: 13, padding: "8px 14px" }}>
              Cancel
            </button>
            <button
              type="submit"
              data-variant="primary"
              disabled={!title.trim() || updateTask.isPending}
              style={{ fontSize: 13, padding: "8px 14px" }}
            >
              {updateTask.isPending ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>

      <ConfirmModal
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete task?"
        message={`"${task.title}" will be permanently removed. This can't be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDelete}
      />
    </div>
  );
}
